import { Link, useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, ChevronLeft, Save, Trash2 } from "lucide-react";

export const BlogList = () => {
  const qc = useQueryClient();
  const { data: posts, isLoading } = useQuery({
    queryKey: ["admin_blog_posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id,slug,title,published,published_at")
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const handleDelete = async (id: string) => {
    if (!confirm("Poistetaanko merkintä?")) return;
    const { error } = await supabase.from("blog_posts").delete().eq("id", id);
    if (error) toast.error(error.message);
    else {
      toast.success("Poistettu");
      qc.invalidateQueries({ queryKey: ["admin_blog_posts"] });
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold">Blogimerkinnät</h2>
        <Button asChild>
          <Link to="/admin/blogi/uusi">
            <Plus className="mr-2 h-4 w-4" /> Uusi merkintä
          </Link>
        </Button>
      </div>
      {isLoading ? (
        <p className="text-muted-foreground">Ladataan...</p>
      ) : posts && posts.length > 0 ? (
        <div className="space-y-2">
          {posts.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-xl border bg-card p-4">
              <Link to={`/admin/blogi/${p.id}`} className="flex-1">
                <p className="font-semibold">{p.title}</p>
                <p className="text-sm text-muted-foreground">
                  {new Date(p.published_at).toLocaleDateString("fi-FI")}
                  {!p.published && " • luonnos"}
                </p>
              </Link>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="rounded-xl border bg-card p-6 text-center text-muted-foreground">
          Ei vielä merkintöjä. Luo ensimmäinen yllä olevasta painikkeesta.
        </p>
      )}
    </div>
  );
};

const slugify = (s: string) =>
  s.toLowerCase()
    .replace(/ä/g, "a").replace(/ö/g, "o").replace(/å/g, "a")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80) || "merkinta";

export const BlogEditor = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === "uusi";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    body: "",
    image_url: "",
    published: true,
    published_at: new Date().toISOString().slice(0, 10),
    category: "omasta_kynasta",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin_blog_post", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("blog_posts").select("*").eq("id", id!).single();
      if (error) throw error;
      return data;
    },
    enabled: !isNew && !!id,
  });

  useEffect(() => {
    if (data) {
      setForm({
        title: data.title,
        slug: data.slug,
        excerpt: data.excerpt ?? "",
        body: data.body ?? "",
        image_url: data.image_url ?? "",
        published: data.published,
        published_at: data.published_at.slice(0, 10),
        category: (data as any).category ?? "omasta_kynasta",
      });
    }
  }, [data]);

  const handleImageUpload = async (file: File) => {
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `blog/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("site-images").upload(path, file);
      if (error) throw error;
      const { data: pub } = supabase.storage.from("site-images").getPublicUrl(path);
      setForm((f) => ({ ...f, image_url: pub.publicUrl }));
      toast.success("Kuva ladattu");
    } catch (e: any) {
      toast.error(e.message || "Kuvan lataus epäonnistui");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const slug = form.slug || slugify(form.title);
      const payload = {
        title: form.title,
        slug,
        excerpt: form.excerpt,
        body: form.body,
        image_url: form.image_url || null,
        published: form.published,
        published_at: new Date(form.published_at).toISOString(),
        category: form.category,
      };
      if (isNew) {
        const { data: ins, error } = await supabase
          .from("blog_posts")
          .insert(payload)
          .select("id")
          .single();
        if (error) throw error;
        toast.success("Merkintä luotu!");
        qc.invalidateQueries({ queryKey: ["admin_blog_posts"] });
        navigate(`/admin/blogi/${ins.id}`);
      } else {
        const { error } = await supabase.from("blog_posts").update(payload).eq("id", id!);
        if (error) throw error;
        toast.success("Tallennettu!");
        qc.invalidateQueries({ queryKey: ["admin_blog_posts"] });
        qc.invalidateQueries({ queryKey: ["admin_blog_post", id] });
        qc.invalidateQueries({ queryKey: ["public_blog_posts"] });
      }
    } catch (e: any) {
      toast.error(e.message || "Tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  };

  if (!isNew && isLoading) return <p className="text-muted-foreground">Ladataan...</p>;

  return (
    <div>
      <Link to="/admin/blogi" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="mr-1 h-4 w-4" /> Takaisin
      </Link>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold">{isNew ? "Uusi merkintä" : "Muokkaa merkintää"}</h2>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Tallennetaan..." : "Tallenna"}
        </Button>
      </div>
      <div className="space-y-4 rounded-2xl border bg-card p-6">
        <div>
          <Label htmlFor="title">Otsikko</Label>
          <Input id="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="slug">Osoite (slug) – jätä tyhjäksi automaattisesti</Label>
          <Input id="slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder={slugify(form.title)} />
        </div>
        <div>
          <Label htmlFor="excerpt">Tiivistelmä</Label>
          <Textarea id="excerpt" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="body">Sisältö (käytä **lihavointi** ja tyhjiä rivejä kappaleille)</Label>
          <Textarea id="body" rows={12} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        </div>
        <div>
          <Label>Kansikuva</Label>
          <div className="flex items-center gap-3">
            <Input type="file" accept="image/*" disabled={uploading} onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleImageUpload(f);
            }} />
            {form.image_url && (
              <img src={form.image_url} alt="" className="h-16 w-16 rounded object-cover" />
            )}
          </div>
        </div>
        <div>
          <Label htmlFor="category">Kategoria</Label>
          <select
            id="category"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="omasta_kynasta">Omasta kynästä</option>
            <option value="lotta_mediassa">Lotta mediassa</option>
            <option value="julkaisut">Julkaisut</option>
          </select>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="published_at">Julkaisupäivä</Label>
            <Input id="published_at" type="date" value={form.published_at} onChange={(e) => setForm({ ...form, published_at: e.target.value })} />
          </div>
          <div className="flex items-end gap-2">
            <input
              id="published"
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              className="h-4 w-4"
            />
            <Label htmlFor="published" className="!mb-0">Julkaistu (näkyy sivustolla)</Label>
          </div>
        </div>
      </div>
    </div>
  );
};
